/* GET /api/me - 15-api-contract.md section 3.11. What the shell needs before
 * it can render - and POST /api/session/tenant, which changes what it says
 * the active tenant is (section 2.2).
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../auth";
import {
  choiceLapsed,
  chosenTenant,
  forgetChoice,
  onChoiceRefused,
  rememberChoice,
  type TenantChoice,
} from "../lib/tenantChoice";
import { fetchJson, postJson } from "./envelope";
import type { Me } from "./types";

export const ME = ["me"] as const;

const ME_FAILED = (status: number) => `your account could not be read (${status})`;

/* A choice that no longer holds is dropped and `/api/me` read again with
 * none, so the shell asks for a choice rather than showing an error for one
 * the person can simply make again. It no longer holds when the API cannot
 * read the token (403) or reads it and names another active tenant, or none
 * (`choiceLapsed`) - a tenant revoked since, or support access removed. */
export async function getMe(): Promise<Me> {
  const choice = chosenTenant();
  if (choice) {
    const response = await apiFetch("/api/me");
    if (response.ok) {
      const me = (await response.json()) as Me;
      if (!choiceLapsed(choice, me)) return me;
    } else if (response.status !== 403) {
      throw new Error(ME_FAILED(response.status));
    }
    forgetChoice();
  }
  return fetchJson<Me>("/api/me", ME_FAILED);
}

/* `enabled` waits for the shell's session check, as the step status does:
 * before it there is no token to send. */
export function useMe(enabled: boolean) {
  return useQuery({ queryKey: ME, queryFn: getMe, enabled });
}

/* A request refused while this tab held a choice reads `/api/me` again, which
 * drops the choice where it has lapsed (`getMe`). Where it has, every cached
 * read is reset, as choosing resets them (`useChooseTenant`): they belong to
 * the tenant just left. A refusal in a tenant the caller still holds leaves
 * the choice, and the cache, as they were. A subscription, so an effect; a
 * failed read is `useMe`'s to report. */
export function useDropLapsedChoice() {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      onChoiceRefused(() => {
        void (async () => {
          try {
            await queryClient.fetchQuery({ queryKey: ME, queryFn: getMe, staleTime: 0 });
          } catch {
            return;
          }
          if (chosenTenant() === null) await queryClient.resetQueries();
        })();
      }),
    [queryClient],
  );
}

/* Choose the tenant this tab acts as. Every cached read belonged to the
 * tenant before, so all of them are reset, not merely refreshed: a screen
 * must never show one customer's data under another's name. */
export function useChooseTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      postJson<TenantChoice>(
        "/api/session/tenant",
        { tenant_id: tenantId },
        (status) => `that tenant could not be chosen (${status})`,
      ),
    onSuccess: async (choice) => {
      rememberChoice(choice);
      await queryClient.resetQueries();
    },
  });
}
